/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 *
 * A row in the bulk Focus Ask card. Only the ask name and row actions are
 * visible; the full field editor opens in the shared modal when the user clicks
 * the Edit icon. Once created, the row stays in place and links to the created
 * ask when the platform returns a URL.
 */
import React from 'react';

import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import EditIcon from '@atlaskit/icon/core/edit';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import SuccessIcon from '@atlaskit/icon/core/status-success';
import { Anchor, Box, Inline, Text } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

import messages from '../messages';
import type { FocusAskFormData } from '../types';

export type BulkFocusAskRowStatus = 'idle' | 'submitting' | 'created' | 'errored';

const styles = cssMap({
	card: {
		width: '100%',
		paddingTop: token('space.100'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.150'),
		display: 'block',
		borderRadius: token('radius.small'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	headerContent: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: token('space.100'),
	},
	nameText: {
		flex: 1,
		minWidth: '0',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
});

export type BulkFocusAskRowProps = {
	rowId: string;
	formData: FocusAskFormData;
	status: BulkFocusAskRowStatus;
	createdLink?: string;
	onEdit: (rowId: string) => void;
	onRemove: (rowId: string) => void;
};

const RowStatusIcon = ({ status }: { status: BulkFocusAskRowStatus }): JSX.Element | null => {
	if (status === 'submitting') {
		return <Spinner size="small" />;
	}
	if (status === 'created') {
		return <SuccessIcon label="" color={token('color.icon.success')} />;
	}
	if (status === 'errored') {
		return <StatusErrorIcon label="" color={token('color.icon.danger')} />;
	}
	return null;
};

const RowStatusLabel = ({ status }: { status: BulkFocusAskRowStatus }): JSX.Element | null => {
	const { formatMessage } = useIntl();
	if (status === 'created') {
		return (
			<Text size="small" color="color.text.subtle">
				{formatMessage(messages.bulkRowStatusCreated)}
			</Text>
		);
	}
	if (status === 'errored') {
		return (
			<Text size="small" color="color.text.danger">
				{formatMessage(messages.bulkRowStatusErrored)}
			</Text>
		);
	}
	return null;
};

export const BulkFocusAskRow = ({
	rowId,
	formData,
	status,
	createdLink,
	onEdit,
	onRemove,
}: BulkFocusAskRowProps): JSX.Element => {
	const { formatMessage } = useIntl();

	const isInteractive = status === 'idle' || status === 'errored';
	const isFinished = status === 'created';
	const displayName = formData.name.trim() || formatMessage(messages.bulkRowUntitled);

	const handleEdit = (e: React.MouseEvent | React.KeyboardEvent) => {
		e.stopPropagation();
		if (!isInteractive) {
			return;
		}
		onEdit(rowId);
	};

	const handleRemove = (e: React.MouseEvent | React.KeyboardEvent) => {
		e.stopPropagation();
		onRemove(rowId);
	};

	return (
		<Box testId={`bulk-focus-ask-row-${rowId}`}>
			<Box xcss={styles.card}>
				<Box xcss={styles.headerContent}>
					<Inline space="space.100" alignBlock="center" xcss={styles.nameText}>
						<RowStatusIcon status={status} />
						<Text weight="semibold">
							{isFinished && createdLink ? (
								<Anchor href={createdLink} target="_blank" rel="noopener noreferrer">
									{displayName}
								</Anchor>
							) : (
								displayName
							)}
						</Text>
						<RowStatusLabel status={status} />
					</Inline>
					<Inline space="space.050" alignBlock="center">
						{isInteractive && (
							<>
								<IconButton
									icon={EditIcon}
									label={formatMessage(messages.bulkRowEditButton)}
									appearance="subtle"
									spacing="compact"
									onClick={handleEdit}
									testId={`bulk-focus-ask-row-edit-${rowId}`}
								/>
								<IconButton
									icon={CrossIcon}
									label={formatMessage(messages.bulkRowRemoveButton)}
									appearance="subtle"
									spacing="compact"
									onClick={handleRemove}
									testId={`bulk-focus-ask-row-remove-${rowId}`}
								/>
							</>
						)}
					</Inline>
				</Box>
			</Box>
		</Box>
	);
};
